import { useState, useRef, useEffect } from 'react';
import RecordTestModal from '../../../../components/RecordTestModal';
import MobileSessionHeader from './MobileSessionHeader';
import MobileGroupList from './MobileGroupList';
import MobileGroupDrillDown from './MobileGroupDrillDown';
import MissingGroupState from './MissingGroupState';
import MobileSessionSheets from './MobileSessionSheets';
import AddAssetSmartForm from './AddAssetSmartForm';
import { deriveMobileGroupCardFields } from '../cards/mobileGroupCardData';
import { deriveAddAssetBoardContext } from '../../lib/addAssetBoardContext';
import { useRecordModal } from '../../hooks/useRecordModal';

const SESSION_SUBTITLE = {
  combined_db_rcd: 'DISTRIBUTION BOARD TESTING',
  combined_exit_emergency: 'EXIT & EMERGENCY TESTING',
  workshop_equipment: 'FITMENTS TESTING',
};

/**
 * Top-level mobile session state machine.
 * TestSessionView hands off here when isMobile && sessionStarted.
 */
export default function MobileSessionView({
  // Identity / navigation
  siteName,
  siteId,
  sessionId,
  elementType,
  onBack,
  // Toolbar
  sessionAssetType,
  assetOptionsForElement,
  onAssetTypeChange,
  equipmentCodeSearch,
  onEquipmentSearchChange,
  // Sort (shared hook state; groupedAssets re-sorts when these change)
  sessionSortBy,
  setSessionSortBy,
  sessionSortOrder,
  setSessionSortOrder,
  // Test context
  sessionTestDate,
  sessionTestType,
  isExitEmergency,
  isSyncing,
  // Assets + results
  groupedAssets = [],
  assetsLoading = false,
  results = {},
  rcdInputs = {},
  setRcdInput,
  dualCheckInputs = {},
  setDualCheckInput,
  // Handlers
  handleInlineRcdSave,
  handleQuickPass,
  registerResult,
  onAssetDetailsUpdated,
  // Timers
  getTimerStatus,
  getTimerRemainingMs,
  startGroupTimer,
  resetGroupTimer,
  // Sheet actions
  onFinish,
  isFinishing = false,
  onCancel,
  canCancelSession = false,
  // Export handlers
  onExportFullReport,
  onExportDefectReport,
  onSendEmailReport,
  siteEmail = '',
  // Called once when the Add Asset form closes after ≥1 create, to reset the
  // asset pager. Not per-create: each reset restarts a sequential page walk
  // (up to ~10 fetches on a 1000+ asset site), and inside the rapid
  // Save-&-add-another loop that would fire every breaker. The list only needs
  // to be correct when the tech returns to it, i.e. on close.
  onAssetCreated,
}) {
  const [screen, setScreen] = useState('list');
  const [activeGroup, setActiveGroup] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  // Did the current Add Asset form session create anything? Per-open flag (not
  // lastAdded, which persists all session) so close only refreshes when needed.
  const addAssetDirtyRef = useRef(false);
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const {
    selectedAsset,
    modalInitialValues,
    openEditModal,
    openFailEditModal,
    handleRecordModalClose,
  } = useRecordModal({ isExitEmergency, sessionTestType });

  // Changing the asset type changes what the groups even are: a board code like
  // 'CBGA' has no counterpart once the session regroups by building, so the active
  // group stops existing and the drill-down has nothing to show. Land on the board
  // list instead — the old group genuinely is not there under the new filter.
  useEffect(() => {
    setScreen('list');
    setActiveGroup(null);
  }, [sessionAssetType]);

  // Ask the app shell to go full-bleed while the session view is mounted. Route
  // sniffing would also flush the /tests pre-flight and /repairs, which want
  // their gutters; letting the session view opt in keeps those untouched.
  useEffect(() => {
    document.body.classList.add('mobile-session-flush');
    return () => document.body.classList.remove('mobile-session-flush');
  }, []);

  const subtitle = SESSION_SUBTITLE[sessionAssetType] || 'TESTING SESSION';

  const totalAssets = groupedAssets.reduce((n, g) => n + g.items.length, 0);
  const totalTested = groupedAssets.reduce(
    (n, g) => n + g.items.filter((a) => results[a.id]).length,
    0,
  );
  const remaining = totalAssets - totalTested;

  // Derive display data per group (used for list tiles + group header scope)
  const groups = groupedAssets.map(({ groupName, items }) => ({
    ...deriveMobileGroupCardFields(groupName, items, sessionAssetType, results),
    groupName,
    items,
  }));

  const activeGroupData = activeGroup
    ? groups.find((g) => g.groupName === activeGroup)
    : null;

  // Board/group context for the Add Asset smart form (pre-fill + default type).
  // See deriveAddAssetBoardContext for the db_number-gating rationale.
  const { boardContext, defaultAssetType } = deriveAddAssetBoardContext(
    activeGroupData?.items,
    lastAdded,
  );

  const openAddAsset = () => {
    addAssetDirtyRef.current = false;
    setAddAssetOpen(true);
  };

  const closeAddAsset = () => {
    setAddAssetOpen(false);
    if (addAssetDirtyRef.current) {
      addAssetDirtyRef.current = false;
      onAssetCreated?.(); // one pager reset for the whole add session
    }
  };

  const handleBack = () => {
    if (screen === 'group') {
      setScreen('list');
      setActiveGroup(null);
    } else {
      onBack();
    }
  };

  const handleTileClick = (groupName) => {
    setActiveGroup(groupName);
    setScreen('group');
  };

  const backToGroupList = () => {
    setScreen('list');
    setActiveGroup(null);
  };

  const searchPlaceholder = screen === 'group' && activeGroup
    ? `Search circuits in ${activeGroup}`
    : 'Search assets';

  return (
    <div className="mobile-session-view">
      <MobileSessionHeader
        siteName={siteName}
        subtitle={subtitle}
        remaining={remaining}
        onBack={handleBack}
        onMenuOpen={() => setSummaryOpen(true)}
        sessionAssetType={sessionAssetType}
        assetOptionsForElement={assetOptionsForElement}
        onAssetTypeChange={onAssetTypeChange}
        searchValue={equipmentCodeSearch}
        onSearchChange={onEquipmentSearchChange}
        searchPlaceholder={searchPlaceholder}
        scopeGroup={screen === 'group' ? activeGroup : null}
        scopeLocation={activeGroupData?.location ?? ''}
      />

      {screen === 'list' && (
        <MobileGroupList
          groups={groups}
          sessionAssetType={sessionAssetType}
          onTileClick={handleTileClick}
          showTimers={isExitEmergency}
          getTimerStatus={getTimerStatus}
          getTimerRemainingMs={getTimerRemainingMs}
          onTimerStart={startGroupTimer}
          onTimerRequestReset={resetGroupTimer}
        />
      )}

      {screen === 'group' && activeGroupData && (
        <MobileGroupDrillDown
          groupName={activeGroup}
          items={activeGroupData.items}
          sessionAssetType={sessionAssetType}
          sessionTestType={sessionTestType}
          sessionTestDate={sessionTestDate}
          isExitEmergency={isExitEmergency}
          isSyncing={isSyncing}
          results={results}
          rcdInputs={rcdInputs}
          setRcdInput={setRcdInput}
          dualCheckInputs={dualCheckInputs}
          setDualCheckInput={setDualCheckInput}
          handleInlineRcdSave={handleInlineRcdSave}
          handleQuickPass={handleQuickPass}
          openEditModal={openEditModal}
          openFailEditModal={openFailEditModal}
          timerStatus={getTimerStatus ? getTimerStatus(activeGroup) : 'idle'}
          timerRemainingMs={getTimerRemainingMs ? getTimerRemainingMs(activeGroup) : 0}
          onTimerStart={() => startGroupTimer?.(activeGroup)}
          onTimerRequestReset={() => resetGroupTimer?.(activeGroup)}
          onAddAsset={openAddAsset}
        />
      )}

      {screen === 'group' && !activeGroupData && (
        <MissingGroupState
          assetsLoading={assetsLoading}
          searchValue={equipmentCodeSearch}
          groupCount={groups.length}
          activeGroup={activeGroup}
          // Same shape the header's search input hands this callback.
          onClearSearch={() => onEquipmentSearchChange?.({ target: { value: '' } })}
          onBackToGroupList={backToGroupList}
        />
      )}

      {/* RecordTestModal overlay */}
      {selectedAsset && (
        <RecordTestModal
          key={`${selectedAsset.id}-${results[selectedAsset.id]?.testId ?? 'new'}-${modalInitialValues?.result ?? 'edit'}`}
          asset={selectedAsset}
          onClose={handleRecordModalClose}
          onRecordSuccess={(assetId, { result, testId, tripTimeMs }) => registerResult(assetId, { result, testId, tripTimeMs })}
          existingTestId={results[selectedAsset.id]?.testId ?? null}
          initialResult={modalInitialValues?.result}
          initialTestType={modalInitialValues?.testType}
          initialDefectsFound={modalInitialValues?.defectsFound}
          initialTripTimeMs={rcdInputs[selectedAsset.id]?.tripTimeMs ?? ''}
          initialPushButton={rcdInputs[selectedAsset.id]?.pushButton ?? null}
          sessionId={sessionId ?? null}
          onAssetDetailsUpdated={onAssetDetailsUpdated}
        />
      )}

      <MobileSessionSheets
        summaryOpen={summaryOpen}
        setSummaryOpen={setSummaryOpen}
        siteName={siteName}
        totalAssets={totalAssets}
        remaining={remaining}
        siteEmail={siteEmail}
        sortBy={sessionSortBy}
        setSortBy={setSessionSortBy}
        sortOrder={sessionSortOrder}
        setSortOrder={setSessionSortOrder}
        isFinishing={isFinishing}
        onAddAsset={openAddAsset}
        onExportFullReport={onExportFullReport}
        onExportDefectReport={onExportDefectReport}
        onSendEmailReport={onSendEmailReport}
        onFinish={onFinish}
        onCancel={onCancel}
        canCancelSession={canCancelSession}
      />

      {/* Add asset smart form (full screen). Conditionally mounted so the form's
          draft state initialises from the CURRENT boardContext each time it opens —
          rendering it unconditionally initialised the draft once at mount, before
          any group was selected, leaving every pre-filled field blank. */}
      {addAssetOpen && (
        <AddAssetSmartForm
          open={addAssetOpen}
          onClose={closeAddAsset}
          siteId={siteId}
          siteName={siteName}
          elementType={elementType}
          sessionAssetType={sessionAssetType}
          defaultAssetType={defaultAssetType}
          boardContext={boardContext}
          groupLabel={activeGroup ?? ''}
          onSuccess={(newAsset) => { setLastAdded(newAsset); addAssetDirtyRef.current = true; }}
        />
      )}
    </div>
  );
}
